// An area's defects on the Condition tab (CONDITION-REPORTS.md "Defects"):
// what's wrong, its cause (which decides who pays), who fixes it and by when,
// its photos, and Open → Rectified → Verified. Saved like any edit, offline
// too; CRM records when each step happened and who verified. Unlike the
// ratings, defects stay open to change once the visit is validated.
import { Button, Label } from '@higgins/ui';
import type { PhotoSources } from '../lib/area-photos';
import { ensureVisitArea, visitAreaOf } from '../lib/condition-records';
import { CHARGE_ROUTES, chargeRouteFor, DEFECT_CAUSES, defectsOfVisitArea, isCharged, nextDefectStep } from '../lib/defect-rules';
import { patchFields, saveRecord, type DeviceRecord, type Fields } from '../lib/device-db';
import { syncNow } from '../lib/outbox';
import { photoRowsOfArea } from '../lib/photo-rows';
import { SavedTextField } from './saved-text-field';
import { SuggestedChoice } from './suggested-choice';

const NAME_MAX = 300;
const DETAILS_MAX = 4000;
const PERSON_MAX = 200;

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

function DefectRow({ defect, photos }: { defect: DeviceRecord; photos: DeviceRecord[] }) {
  const save = (changes: Fields) => patchFields(defect.id, changes);
  const cause = typeof defect.fields.hig_cause === 'string' ? defect.fields.hig_cause : null;
  const step = nextDefectStep(defect.fields.hig_status);

  return (
    <div className="space-y-2 rounded-md border border-slate-200 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700">{text(defect.fields.hig_status) || 'Open'}</span>
        <Button variant="outline" size="sm" className="min-h-11" onClick={() => void save({ statecode: 'Inactive' })}>
          Take off
        </Button>
      </div>
      <SavedTextField id={`defect-name-${defect.id}`} label="What’s wrong" value={text(defect.fields.hig_name)} onSave={(v) => save({ hig_name: v })} placeholder="What’s wrong, e.g. Peeling at the entry doors" maxLength={NAME_MAX} />
      <SavedTextField id={`defect-details-${defect.id}`} label="Details" rows={2} value={text(defect.fields.hig_details)} onSave={(v) => save({ hig_details: v })} placeholder="Details" maxLength={DETAILS_MAX} />
      <SuggestedChoice label="Cause" options={DEFECT_CAUSES} chosen={cause} suggested={null} onChoose={(c) => void save({ hig_cause: c, hig_chargeroute: chargeRouteFor(c) })} />
      {isCharged(cause) && (
        <SuggestedChoice label="Charged as" options={CHARGE_ROUTES} chosen={typeof defect.fields.hig_chargeroute === 'string' ? defect.fields.hig_chargeroute : null} suggested={null} onChoose={(r) => void save({ hig_chargeroute: r })} />
      )}
      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <Label htmlFor={`defect-assigned-${defect.id}`} className="text-xs text-slate-600">Assigned to</Label>
          <SavedTextField id={`defect-assigned-${defect.id}`} label="Assigned to" value={text(defect.fields.hig_assignedto)} onSave={(v) => save({ hig_assignedto: v })} maxLength={PERSON_MAX} />
        </div>
        <div>
          <Label htmlFor={`defect-due-${defect.id}`} className="text-xs text-slate-600">Due</Label>
          <input id={`defect-due-${defect.id}`} type="date" value={text(defect.fields.hig_dueon)} onChange={(e) => void save({ hig_dueon: e.target.value || null })} className="block min-h-11 w-full rounded-md border border-slate-300 px-2 text-sm" />
        </div>
      </div>
      {photos.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Photos of this defect">
          <span className="text-xs text-slate-500">Photos</span>
          {photos.map((p) => {
            const on = p.fields.hig_defectid === defect.id;
            return (
              <button key={p.id} type="button" aria-pressed={on} onClick={() => void patchFields(p.id, { hig_defectid: on ? null : defect.id })} className={`min-h-11 rounded-full border px-3 text-sm ${on ? 'border-primary bg-primary text-primary-foreground' : 'border-slate-300 text-slate-700'}`}>
                {text(p.fields.hig_name).replace(/\.jpe?g$/i, '')}
              </button>
            );
          })}
        </div>
      )}
      <div className="flex flex-wrap items-end gap-2">
        {step?.to === 'Verified' && (
          <div className="min-w-48 flex-1">
            <Label htmlFor={`defect-verifier-${defect.id}`} className="text-xs text-slate-600">Client who verified it</Label>
            <SavedTextField id={`defect-verifier-${defect.id}`} label="Client who verified it" value={text(defect.fields.hig_clientverifiername)} onSave={(v) => save({ hig_clientverifiername: v })} maxLength={PERSON_MAX} />
          </div>
        )}
        {step && (
          <Button size="sm" className="min-h-11" onClick={() => void save({ hig_status: step.to })}>
            {step.label}
          </Button>
        )}
        {text(defect.fields.hig_status) !== 'Open' && text(defect.fields.hig_status) && (
          <Button variant="outline" size="sm" className="min-h-11" onClick={() => void save({ hig_status: 'Open' })}>
            Reopen
          </Button>
        )}
      </div>
    </div>
  );
}

/** `photoSources`: the job's photos, read once by the page. */
export function DefectsSection({ visit, area, records, photoSources }: { visit: DeviceRecord; area: DeviceRecord; records: DeviceRecord[]; photoSources: PhotoSources }) {
  const visitArea = visitAreaOf(records, area.id);
  const defects = defectsOfVisitArea(records, visitArea?.id ?? null);
  const photos = photoRowsOfArea(photoSources.records, area.id);

  async function add() {
    const owner = await ensureVisitArea(visit, area);
    await saveRecord({ id: crypto.randomUUID(), table: 'defect', jobId: area.jobId, parentId: visit.id, etag: null, fields: { hig_name: '', hig_visitareaid: owner.id, hig_status: 'Open' } });
    void syncNow();
  }

  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Label>Defects</Label>
          <p className="text-xs text-slate-500">Each defect&rsquo;s cause decides who pays: ours, someone else&rsquo;s (charged), or the client&rsquo;s own asset.</p>
        </div>
        <Button variant="outline" size="sm" className="min-h-11" onClick={() => void add()}>
          Add defect
        </Button>
      </div>
      {defects.map((d) => (
        <DefectRow key={d.id} defect={d} photos={photos} />
      ))}
    </div>
  );
}
